import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BookingsDisplaySettings,
  GetBookingsDisplaySettingsByWidgetIdentifiers,
  GetBookingsDisplaySettingsByWidgetOptions,
  UpsertBookingsDisplaySettings,
  UpsertBookingsDisplaySettingsApplicationErrors,
  UpsertBookingsDisplaySettingsIdentifiers,
  UpsertBookingsDisplaySettingsResponse,
  deleteBookingsDisplaySettings as universalDeleteBookingsDisplaySettings,
  getBookingsDisplaySettingsByWidget as universalGetBookingsDisplaySettingsByWidget,
  upsertBookingsDisplaySettings as universalUpsertBookingsDisplaySettings,
} from './bookings-display-settings-v1-bookings-display-settings-display-settings.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/bookings' };

/** @internal */
export function getBookingsDisplaySettingsByWidget(
  httpClient: HttpClient
): GetBookingsDisplaySettingsByWidgetSignature {
  return (
    identifiers: NonNullablePaths<
      GetBookingsDisplaySettingsByWidgetIdentifiers,
      `appId` | `widgetType`,
      2
    >,
    options?: GetBookingsDisplaySettingsByWidgetOptions
  ) =>
    universalGetBookingsDisplaySettingsByWidget(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetBookingsDisplaySettingsByWidgetSignature {
  /**
   * A single-item getter addressed by the natural key (app_id + widget_type
   * [+ custom_widget_key]); annotated as the GET_ITEM CRUD flavor. The standard
   * id-path Get is intentionally not exposed — `id` is server-derived (see
   * TECH_DESIGN D5/D7).
   * @returns The widget's settings row, with defaults backfilled per field. Never empty:
   * a synthesised, fully defaulted row (with no `revision`) is returned when
   * nothing has been saved for the widget.
   */
  (
    identifiers: NonNullablePaths<
      GetBookingsDisplaySettingsByWidgetIdentifiers,
      `appId` | `widgetType`,
      2
    >,
    options?: GetBookingsDisplaySettingsByWidgetOptions
  ): Promise<
    NonNullablePaths<
      BookingsDisplaySettings,
      | `sidePanelView.notes.displayMode`
      | `appId`
      | `widgetType`
      | `customWidgetKey`,
      4
    >
  >;
}

/** @internal */
export function upsertBookingsDisplaySettings(
  httpClient: HttpClient
): UpsertBookingsDisplaySettingsSignature {
  return (
    identifiers: NonNullablePaths<
      UpsertBookingsDisplaySettingsIdentifiers,
      `bookingsDisplaySettingsAppId` | `bookingsDisplaySettingsWidgetType`,
      2
    >,
    bookingsDisplaySettings: UpsertBookingsDisplaySettings
  ) =>
    universalUpsertBookingsDisplaySettings(
      identifiers,
      bookingsDisplaySettings,
      // @ts-ignore
      { httpClient }
    );
}

interface UpsertBookingsDisplaySettingsSignature {
  /**
   * Atomic insert-or-update of the BookingsDisplaySettings row identified by
   * (app_id, widget_type [+ custom_widget_key for CUSTOM]); the site is implicit.
   * Single Save action on the client.
   *
   * Keyed by the natural key: if no row matches it is created, otherwise it is
   * updated in place (partial update via `field_mask`). `revision` is
   * server-assigned and read-only — Upsert does not use optimistic-concurrency
   * control (that belongs to Update / AIP-4006), so concurrent writes to the same
   * row are last-write-wins.
   *
   * `extended_fields` are validated at write time against the JSON Schema the app
   * registered for this entity in Dev Center; a payload that violates that schema
   * is rejected with `INVALID_EXTENDED_FIELDS`.
   */
  (
    identifiers: NonNullablePaths<
      UpsertBookingsDisplaySettingsIdentifiers,
      `bookingsDisplaySettingsAppId` | `bookingsDisplaySettingsWidgetType`,
      2
    >,
    bookingsDisplaySettings: UpsertBookingsDisplaySettings
  ): Promise<
    NonNullablePaths<
      UpsertBookingsDisplaySettingsResponse,
      | `bookingsDisplaySettings.sidePanelView.notes.displayMode`
      | `bookingsDisplaySettings.appId`
      | `bookingsDisplaySettings.widgetType`
      | `bookingsDisplaySettings.customWidgetKey`,
      5
    > & {
      __applicationErrorsType?: UpsertBookingsDisplaySettingsApplicationErrors;
    }
  >;
}

/** @internal */
export function deleteBookingsDisplaySettings(
  httpClient: HttpClient
): DeleteBookingsDisplaySettingsSignature {
  return (bookingsDisplaySettingsId: string) =>
    universalDeleteBookingsDisplaySettings(
      bookingsDisplaySettingsId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteBookingsDisplaySettingsSignature {
  /**
   * Deletes the BookingsDisplaySettings row with the given ID, resetting that
   * widget to its server defaults: a subsequent GetBookingsDisplaySettingsByWidget
   * returns a freshly synthesised default row (with no `revision`).
   *
   * Use the `id` from a previous Upsert response, or from a
   * GetBookingsDisplaySettingsByWidget response for a saved row (a synthesised
   * default row carries no `id`). Idempotent: deleting a row that isn't stored is
   * a no-op success — the widget is already at its defaults.
   * @param - ID of the BookingsDisplaySettings row to delete. Obtain it from a previous
   * UpsertBookingsDisplaySettings response, or from a
   * GetBookingsDisplaySettingsByWidget response for a saved row (a synthesised
   * default row carries no `id`).
   */
  (bookingsDisplaySettingsId: string): Promise<void>;
}

export {
  AccountInfo,
  ActionEvent,
  AddOnsSection,
  AssignedStaffMemberSection,
  BookingsDisplaySettings,
  BookingsDisplaySettingsWidgetSettingsOneOf,
  DeleteBookingsDisplaySettingsRequest,
  DeleteBookingsDisplaySettingsResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetBookingsDisplaySettingsByWidgetIdentifiers,
  GetBookingsDisplaySettingsByWidgetOptions,
  GetBookingsDisplaySettingsByWidgetRequest,
  GetBookingsDisplaySettingsByWidgetResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  LocationSection,
  MessageEnvelope,
  NotesDisplayMode,
  NotesSection,
  PriceSection,
  RequestedStaffMemberMethodSection,
  ResourcesSection,
  RestoreInfo,
  SidePanelView,
  UpsertBookingsDisplaySettings,
  UpsertBookingsDisplaySettingsIdentifiers,
  UpsertBookingsDisplaySettingsRequest,
  UpsertBookingsDisplaySettingsResponse,
  WebhookIdentityType,
  WidgetType,
} from './bookings-display-settings-v1-bookings-display-settings-display-settings.universal.js';
